import { getCountByStatus, useGetAllDocuments } from "@/api/document/documents";
import NewDocumentDialog from "@/components/document/NewDocumentDialog";
import NewItemDialogTrigger from "@/components/modal/NewItemDialogTrigger";
import StatusTableTab from "@/components/StatusTableTab";
import { AdvancedSearch } from "@/components/table/AdvancedSearch";
import { AdvancedSearchChip } from "@/components/table/AdvancedSearchChip";
import { AdvancedSort } from "@/components/table/AdvancedSort";
import { GlobalSearch } from "@/components/table/GlobalSearch";
import { DataTable } from "@/components/ui/datatable";
import {
  documentsAdvancedSearchConfig,
  documentsColumns,
} from "@/config/dashboard/documents";
import { ColumnFormatted, generateTable } from "@/config/dashboard/general";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { Document } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const STORAGE_KEY = "document-table-preferences";

const flatVisibleDocumentsColumns = documentsColumns.flatMap(
  (column: ColumnFormatted) => (column.visible ? column.key : []),
);

const DocumentDashboard = () => {
  const { t } = useTranslation();

  const {
    countByStatus,
    currentCount,
    pagination,
    setPagination,
    fetchParams,
    filterStatus,
    setFilterStatus,
  } = useDashboardParams<Document>({
    resourceName: "document",
    resourceCounter: getCountByStatus,
    statusField: "documentsPublicationStatus",
  });

  const navigate = useNavigate();

  const { data } = useGetAllDocuments(fetchParams);

  const columns: ColumnDef<Document, any>[] = useMemo(() => {
    return generateTable(documentsColumns, "document");
  }, []);

  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const { order } = JSON.parse(saved);
      return order || flatVisibleDocumentsColumns;
    }
    return flatVisibleDocumentsColumns;
  });

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { visibility } = JSON.parse(saved);
        return (
          visibility || {
            ...documentsColumns.reduce(
              (acc, column) => ({
                ...acc,
                [column.key]: column.visible || false,
              }),
              {},
            ),
          }
        );
      }
      return {
        ...documentsColumns.reduce(
          (acc, column) => ({
            ...acc,
            [column.key]: column.visible || false,
          }),
          {},
        ),
      };
    },
  );

  return (
    <div className="flex flex-col items-start gap-3">
      <div className="flex justify-between  items-center w-full">
        <p className="text-[32px] font-[600]">{t("dashboardTitleDocuments")}</p>
        <GlobalSearch
          config={documentsAdvancedSearchConfig}
          data={data ?? []}
        />
      </div>
      <div className="flex flex-row justify-between w-full">
        <StatusTableTab
          filterStatus={filterStatus}
          onValueChange={setFilterStatus}
          countByStatus={countByStatus}
        />
        <div className="flex flex-row gap-2">
          <AdvancedSearch
            config={documentsAdvancedSearchConfig}
            resource="documents"
          />
          <AdvancedSort config={documentsAdvancedSearchConfig} />
          <NewItemDialogTrigger
            title={t("createDdocument")}
            Dialog={NewDocumentDialog}
          />
        </div>
      </div>
      <div className="w-full flex flex-row">
        <AdvancedSearchChip />
      </div>
      <div className="w-full">
        <DataTable
          onRowClick={(row) => navigate(`/edit/document/${row.original.id}`)}
          pagination={{
            pagination: pagination,
            setPage: setPagination,
            totalSize: currentCount,
          }}
          columns={columns}
          data={data ?? []}
          storageKey={STORAGE_KEY}
          options={{
            enableColumnResizing: true,
            state: {
              columnVisibility: columnVisibility,
              columnOrder: columnsOrder,
            },
            onColumnVisibilityChange: setColumnVisibility,
            onColumnOrderChange: setColumnsOrder,
          }}
        />
      </div>
    </div>
  );
};

export default DocumentDashboard;
